import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { LogOut, User, LayoutDashboard, ShoppingBag, Zap } from 'lucide-react';
import FallbackImage from '@/components/FallbackImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Landing / Home for MACRON · SUDEPORTE.
 * Client: Deportes Tomás Elda — Club: Elda Unión CF — Season: 26/27
 * Design language: monochrome (black/white) inspired by b2b.macron.com and sudeporte.com.
 */
const Landing = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(`${API}/products`, { headers });
        setFeatured(Array.isArray(data) ? data.slice(0, 8) : []);
      } catch (e) {
        setFeatured([]);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Header */}
      <header className="bg-black border-b border-neutral-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-4 shrink-0" data-testid="brand-link">
              <img src="/sudeporte-logo.png" alt="Sudeporte" className="h-10 sm:h-11" />
              <span className="hidden sm:block h-9 w-px bg-neutral-700" />
              <img src="/macron-msh-neg-h44.png" alt="Macron Sports Hub" className="hidden sm:block h-8" />
            </Link>

            <nav className="flex items-center gap-2 sm:gap-3">
              {user ? (
                <>
                  <span className="hidden md:inline text-neutral-400 text-sm mr-2">
                    {user.business_name}
                  </span>
                  <Link to="/my-orders">
                    <Button data-testid="nav-orders-btn" variant="ghost" className="text-white hover:bg-neutral-800">
                      <User className="w-4 h-4 mr-2" />
                      Mis Pedidos
                    </Button>
                  </Link>
                  {(user.role === 'admin' || user.role === 'manager') && (
                    <Link to="/admin">
                      <Button data-testid="nav-admin-btn" variant="ghost" className="text-white hover:bg-neutral-800">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Button data-testid="logout-btn" onClick={onLogout} variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                    <LogOut className="w-4 h-4 mr-2" />
                    Salir
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button data-testid="login-btn" variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                      Iniciar Sesión
                    </Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="container mx-auto px-4 py-20 sm:py-28">
          <div className="max-w-5xl mx-auto text-center space-y-6">
            {/* club badges */}
            <div className="flex items-center justify-center gap-6 sm:gap-10 mb-6">
              <img
                src="/elda-escudo.png"
                alt="Elda Unión CF"
                className="h-24 sm:h-28 drop-shadow-md"
                data-testid="club-crest"
              />
              <div className="text-left">
                <p className="uppercase tracking-widest text-xs sm:text-sm text-neutral-500">Temporada 2026 / 2027</p>
                <h2 className="text-2xl sm:text-3xl font-black leading-tight">
                  ELDA UNIÓN CF
                </h2>
                <p className="text-neutral-600 text-sm mt-1">Equipación oficial · MACRON</p>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Pedidos oficiales de club
              <span className="block mt-2 text-neutral-500">para Deportes Tomás</span>
            </h1>
            <p className="text-lg sm:text-xl text-neutral-600 max-w-2xl mx-auto">
              Catálogo cerrado de la temporada <strong className="text-black">26/27</strong>. Referencias,
              tallas y precios preacordados con <strong>Sudeporte</strong>. Un solo pedido, un solo circuito.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-6">
              {user ? (
                <>
                  <Button
                    data-testid="hero-catalog-btn"
                    onClick={() => navigate('/catalog')}
                    size="lg"
                    className="px-8 py-6 text-lg bg-black hover:bg-neutral-800 text-white font-bold"
                  >
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Ver catálogo 26/27
                  </Button>
                  <Button
                    data-testid="hero-orders-btn"
                    onClick={() => navigate('/my-orders')}
                    size="lg"
                    variant="outline"
                    className="px-8 py-6 text-lg border-2 border-black text-black hover:bg-black hover:text-white font-bold"
                  >
                    Mis Pedidos
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    data-testid="hero-public-catalog-btn"
                    onClick={() => navigate('/catalog')}
                    size="lg"
                    className="px-8 py-6 text-lg bg-black hover:bg-neutral-800 text-white font-bold"
                  >
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Ver catálogo 26/27
                  </Button>
                  <Button
                    data-testid="hero-login-btn"
                    onClick={() => navigate('/login')}
                    size="lg"
                    variant="outline"
                    className="px-8 py-6 text-lg border-2 border-black text-black hover:bg-black hover:text-white font-bold"
                  >
                    Acceder para hacer pedido
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-neutral-100/60 to-transparent pointer-events-none" />
      </section>

      {/* Info Section — Circuito comercial */}
      <section className="bg-neutral-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-4">
            <FlowCard title="MACRON" subtitle="Marca deportiva" note="Prendas oficiales de club y equipación técnica" strong />
            <FlowCard title="SUDEPORTE" subtitle="Delegación Macron Alicante" note="Gestiona el pedido de compra y sirve al cliente" />
            <FlowCard title="DEPORTES TOMÁS" subtitle="Cliente · Elda" note="Único cliente autorizado a comprar la equipación de Elda Unión CF" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <Feature
              icon={<ShoppingBag className="w-6 h-6" />}
              title="Catálogo cerrado 26/27"
              text="37 referencias oficiales de MACRON para Elda Unión CF con tallas y precio final aplicado."
            />
            <Feature
              icon={<Zap className="w-6 h-6" />}
              title="Doble email automático"
              text="Cada pedido dispara la confirmación de venta y el CSV de compra a MACRON en el mismo circuito."
            />
            <Feature
              icon={<LayoutDashboard className="w-6 h-6" />}
              title="Panel de administración"
              text="Laura y Sudeporte gestionan referencias, precios y estado de pedidos desde un único panel."
            />
          </div>
        </div>
      </section>

      {/* Featured products preview */}
      {featured.length > 0 && (
        <section className="bg-neutral-50 py-16 border-t border-neutral-200">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500">Catálogo 26/27</p>
                <h3 className="text-3xl font-black text-black">Equipación MACRON · Elda Unión CF</h3>
              </div>
              <Link
                to="/catalog"
                data-testid="see-all-catalog"
                className="hidden sm:inline text-sm uppercase tracking-widest font-bold text-black hover:underline"
              >
                Ver todo el catálogo →
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {featured.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${encodeURIComponent(p.id)}`}
                  data-testid={`featured-card-${p.id}`}
                  className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:border-black transition-all duration-200 hover:-translate-y-1"
                >
                  <div className="relative h-56 bg-neutral-50">
                    <FallbackImage
                      images={p.images}
                      alt={p.display_name || p.model_name}
                      className="w-full h-full object-contain p-4"
                    />
                    <span className="absolute top-2 left-2 bg-black text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                      MACRON
                    </span>
                  </div>
                  <div className="p-4 border-t border-neutral-100">
                    <p className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">
                      {p.familia || 'Macron'}{p.subfamilia ? ` · ${p.subfamilia}` : ''}
                    </p>
                    <h4 className="text-base font-black text-black leading-tight">{p.model_name}</h4>
                    <p className="text-neutral-700 text-sm">{p.color}</p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8 sm:hidden">
              <Link to="/catalog">
                <Button size="lg" className="bg-black hover:bg-neutral-800 text-white font-bold">
                  Ver todo el catálogo
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}


      {/* Footer */}
      <footer className="bg-black text-white py-14">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-8">
            <div>
              <img src="/sudeporte-logo.png" alt="Sudeporte" className="h-10 mb-4" />
              <p className="text-neutral-400 text-sm max-w-xs">
                Plataforma B2B de Sudeporte para clubes que visten MACRON. Circuito
                exclusivo de la temporada 26/27.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">Sudeporte · Macron</h3>
              <div className="text-neutral-300 text-sm space-y-2">
                <p className="font-semibold text-white">Pedidos y facturación</p>
                <p><a href="mailto:pedidos@sudeporte.com" className="text-white hover:underline">pedidos@sudeporte.com</a></p>
                <p><a href="mailto:laura@sudeporte.com" className="text-white hover:underline">laura@sudeporte.com</a></p>
                <p><a href="mailto:macron@sudeporte.com" className="text-white hover:underline">macron@sudeporte.com</a></p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">Cliente autorizado</h3>
              <div className="text-neutral-300 text-sm space-y-2">
                <p className="font-semibold text-white">Deportes Tomás Elda</p>
                <p><a href="mailto:deportestomasinfo@gmail.com" className="text-white hover:underline">deportestomasinfo@gmail.com</a></p>
                <p className="pt-2 text-neutral-400">Compras oficiales del club Elda Unión CF</p>
              </div>
            </div>
          </div>
          <div className="border-t border-neutral-800 pt-6 text-center text-neutral-500 text-sm">
            © {new Date().getFullYear()} SUDEPORTE · SUDEPORTE — Todos los derechos reservados. MACRON® es marca registrada de sus respectivos titulares.
          </div>
        </div>
      </footer>
    </div>
  );
};

const FlowCard = ({ title, subtitle, note, strong }) => (
  <div className={`rounded-xl p-6 border ${strong ? 'border-black bg-black text-white' : 'border-neutral-200 bg-white text-black'}`}>
    <p className={`text-xs uppercase tracking-widest ${strong ? 'text-neutral-400' : 'text-neutral-500'}`}>{subtitle}</p>
    <h3 className="text-2xl font-black mt-1">{title}</h3>
    <p className={`text-sm mt-3 ${strong ? 'text-neutral-300' : 'text-neutral-600'}`}>{note}</p>
  </div>
);

const Feature = ({ icon, title, text }) => (
  <div className="rounded-xl p-6 border border-neutral-200 bg-white hover:border-black transition-colors">
    <div className="w-12 h-12 rounded-lg bg-black text-white flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="text-lg font-bold mb-2">{title}</h3>
    <p className="text-neutral-600 text-sm leading-relaxed">{text}</p>
  </div>
);

export default Landing;
